import { type FC, type ReactNode, useMemo } from "react";

import { serverGetSectionScoreTable } from "api/api-server-assessment";
import { useCanAccessStudentSectionScores, useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import { selectCurrentSection } from "store/selectors";
import { usePromiseEffect } from "store/store-hooks";
import { SectionScoreTableContext } from "utils/hooks/use-section-score-table";

/**
 * Fetches the section score table for the current section once, and exposes the result
 * to descendants via context. Multiple descendant components can read the data via
 * `useSectionScoreTable()` without triggering additional network requests.
 */
export const SectionScoreTableProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const section = useAppSelector(selectCurrentSection);
  const sectionId = section?.id;
  const sectionScoreEnabled = section?.section_score_enabled;
  const { canAuthorSection } = useCapabilities();
  const canAccessStudentSectionScores = useCanAccessStudentSectionScores();
  const shouldFetchSectionScores =
    Boolean(sectionId && sectionScoreEnabled) &&
    (canAuthorSection || canAccessStudentSectionScores);

  const { data, isLoading, error } = usePromiseEffect(
    async () => (shouldFetchSectionScores ? serverGetSectionScoreTable(sectionId!) : null),
    [sectionId, shouldFetchSectionScores],
  );

  const value = useMemo(() => ({ rows: data, isLoading, error }), [data, isLoading, error]);

  return (
    <SectionScoreTableContext.Provider value={value}>{children}</SectionScoreTableContext.Provider>
  );
};
